import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { Box, Typography, IconButton, useTheme, FormHelperText } from "@mui/material";
import {
  CalendarToday,
  ChevronLeft,
  ChevronRight,
  UnfoldMore,
  Close as CloseIcon,
} from "@mui/icons-material";
import { formatDateForInput } from "../../utils/dateUtils";

/* ─── helpers ─── */
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

/** Parse "YYYY-MM-DD" → local Date (avoids timezone shift) */
const parseLocal = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, m - 1, d);
};

/** Format Date → "YYYY-MM-DD" */
const toIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

/** Format Date → display string like "November 21, 2023" */
const toDisplay = (d) =>
  `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;

/** Is same calendar day? */
const isSameDay = (a, b) =>
  a && b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

/* ───────────────────────────────────────────────── */
/*  DatePicker                                        */
/* ───────────────────────────────────────────────── */
const DatePicker = ({
  // Date value and change handler
  value,
  onChange,

  // Date range constraints
  minDate,
  maxDate,

  // TextField-compatible props
  label,
  error = false,
  helperText,
  disabled = false,
  fullWidth = true,
  required = false,

  // Styling
  sx = {},
  size = "small",

  // Advanced
  placeholder = "Select date",
  clearable = false,
}) => {
  const theme = useTheme();

  /* business logic */
  const { minDateStr, maxDateStr, currentDate } = useMemo(() => {
    const min = formatDateForInput(minDate);
    const max = formatDateForInput(maxDate);
    const today = formatDateForInput(new Date());
    let defaultDate = value || "";

    // If no value provided and max date is in the future, default to today
    if (!value && max && today && max > today) {
      defaultDate = today;
    }

    return {
      minDateStr: min,
      maxDateStr: max,
      currentDate: value || defaultDate || "",
    };
  }, [minDate, maxDate, value]);

  const selectedObj = parseLocal(currentDate);
  const minObj = parseLocal(minDateStr);
  const maxObj = parseLocal(maxDateStr);

  /* ── local UI state ── */
  const [open, setOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 272 });
  const [viewYear, setViewYear] = useState(
    selectedObj ? selectedObj.getFullYear() : new Date().getFullYear()
  );
  const [viewMonth, setViewMonth] = useState(
    selectedObj ? selectedObj.getMonth() : new Date().getMonth()
  );
  const wrapperRef = useRef(null);

  /* close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* sync view when selectedDate changes externally */
  useEffect(() => {
    if (selectedObj) {
      setViewYear(selectedObj.getFullYear());
      setViewMonth(selectedObj.getMonth());
    }
  }, [currentDate]);

  /* ── navigation ── */
  const prevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
  }, []);

  const nextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
  }, []);

  /* ── build calendar grid ── */
  const calendarDays = useMemo(() => {
    const first = new Date(viewYear, viewMonth, 1);
    // Monday = 0 … Sunday = 6
    let startDow = first.getDay() - 1;
    if (startDow < 0) startDow = 6;

    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const daysInPrev = new Date(viewYear, viewMonth, 0).getDate();

    const cells = [];

    // trailing days of previous month
    for (let i = startDow - 1; i >= 0; i--) {
      cells.push({
        day: daysInPrev - i,
        current: false,
        date: new Date(viewYear, viewMonth - 1, daysInPrev - i),
      });
    }

    // days of current month
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push({
        day: d,
        current: true,
        date: new Date(viewYear, viewMonth, d),
      });
    }

    // leading days of next month to fill last row
    const remaining = 7 - (cells.length % 7);
    if (remaining < 7) {
      for (let d = 1; d <= remaining; d++) {
        cells.push({
          day: d,
          current: false,
          date: new Date(viewYear, viewMonth + 1, d),
        });
      }
    }

    return cells;
  }, [viewYear, viewMonth]);

  /* ── is date within allowed range? ── */
  const isDisabled = (date) => {
    if (minObj && date < new Date(minObj.getFullYear(), minObj.getMonth(), minObj.getDate())) return true;
    if (maxObj && date > new Date(maxObj.getFullYear(), maxObj.getMonth(), maxObj.getDate())) return true;
    return false;
  };

  const today = new Date();

  /* ── select handler ── */
  const handleSelect = (cell) => {
    if (isDisabled(cell.date)) return;
    onChange?.(toIso(cell.date));
    setOpen(false);
  };

  // Calculate input width based on size
  const inputWidth = fullWidth ? "100%" : size === "small" ? 280 : 320;
  const inputPadding = size === "small" ? 1 : 1.5;
  const fontSize = size === "small" ? "0.875rem" : "1rem";

  // Determine border color based on state
  const getBorderColor = () => {
    if (error) return theme.palette.error.main;
    if (open) return theme.palette._blue?.main || theme.palette.primary.main;
    return theme.customColors?.border?.strong || "#D1D5DB";
  };

  /* ───────── render ───────── */
  return (
    <Box
      sx={{
        width: fullWidth ? "100%" : "auto",
        maxWidth: "100%",
        boxSizing: "border-box",
        ...sx,
      }}
    >
      {/* Label */}
      {label && (
        <Typography
          variant="body2"
          sx={{
            mb: 0.75,
            fontWeight: 600,
            fontSize: "0.875rem",
            color: error ? theme.palette.error.main : theme.customColors?.text?.strong ?? "#374151",
          }}
        >
          {label}{required && " *"}
        </Typography>
      )}

      <Box
        ref={wrapperRef}
        sx={{
          position: "relative",
          display: "inline-block",
          width: fullWidth ? "100%" : "auto",
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* ── Input trigger ── */}
        <Box
          onClick={() => {
            if (disabled) return;
            if (!open && wrapperRef.current) {
              const rect = wrapperRef.current.getBoundingClientRect();
              const dropdownHeight = 320; // Approximate dropdown height

              // Calculate vertical positioning
              const spaceBelow = window.innerHeight - rect.bottom;
              const spaceAbove = rect.top;
              const shouldFlipUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

              // Use input width for dropdown, with responsive adjustments and a max width cap
              const spaceRight = window.innerWidth - rect.left;
              const maxDropdownWidth = 360; // prevent calendar from becoming too wide
              let dropdownWidth = Math.min(rect.width, spaceRight - 16, maxDropdownWidth); // Match input width up to max, or fit in viewport
              let leftPosition = rect.left;

              // Ensure minimum readable width
              if (dropdownWidth < 220) {
                dropdownWidth = Math.min(272, rect.right - 16);
                leftPosition = rect.right - dropdownWidth;
              }

              setDropdownPosition({
                top: shouldFlipUp ? rect.top - dropdownHeight - 6 : rect.bottom + 6,
                left: Math.max(16, leftPosition), // At least 16px from left edge
                width: Math.max(220, dropdownWidth), // Minimum 220px width
              });
            }
            setOpen((o) => !o);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            width: inputWidth,
            maxWidth: "100%",
            boxSizing: "border-box",
            px: inputPadding,
            py: size === "small" ? 1 : 1.25,
            borderRadius: "12px",
            bgcolor: disabled ? theme.customColors?.background?.light : theme.customColors?.background?.main,
            border: `1.5px solid ${getBorderColor()}`,
            boxShadow: open && !error && !disabled
              ? `0 0 0 3px ${theme.customColors?.status?.info?.bg || "rgba(59, 130, 246, 0.1)"}`
              : "0 1px 2px rgba(0,0,0,0.04)",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            transition: "all 0.2s ease",
            "&:hover": disabled ? {} : {
              borderColor: error ? theme.palette.error.main : "#90caf9",
            },
          }}
        >
          <CalendarToday sx={{ fontSize: size === "small" ? "1.125rem" : "1.25rem", color: theme.customColors?.text?.secondary }} />
          <Typography
            sx={{
              flex: 1,
              fontSize,
              fontWeight: 600,
              color: selectedObj ? theme.customColors?.text?.primary : theme.customColors?.text?.secondary,
              userSelect: "none",
            }}
          >
            {selectedObj ? toDisplay(selectedObj) : placeholder}
          </Typography>
          {clearable && selectedObj ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(null);
                setOpen(false);
              }}
              sx={{ p: 0.25, color: theme.customColors?.text?.secondary, "&:hover": { color: "#ef4444" } }}
            >
              <CloseIcon sx={{ fontSize: size === "small" ? "1rem" : "1.125rem" }} />
            </IconButton>
          ) : (
            <UnfoldMore sx={{ fontSize: size === "small" ? "1.125rem" : "1.25rem", color: theme.customColors?.text?.secondary }} />
          )}
        </Box>

        {/* ── Dropdown calendar ── */}
        {open && !disabled && (
          <Box
            sx={{
              position: "fixed",
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              zIndex: 1500,
              width: dropdownPosition.width,
              bgcolor: theme.customColors?.background?.main,
              borderRadius: "12px",
              border: `1px solid ${theme.customColors?.border?.strong}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              p: 2,
              animation: "fadeSlideIn 0.15s ease-out",
              "@keyframes fadeSlideIn": {
                from: { opacity: 0, transform: "translateY(-6px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            {/* month / year header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <IconButton size="small" onClick={prevMonth} sx={{ color: theme.customColors?.text?.secondary }}>
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Typography
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 700,
                  color: theme.customColors?.text?.primary,
                }}
              >
                {MONTHS[viewMonth]} {viewYear}
              </Typography>
              <IconButton size="small" onClick={nextMonth} sx={{ color: theme.customColors?.text?.secondary }}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </Box>

            {/* day-of-week headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                mb: 0.5,
              }}
            >
              {DAYS.map((d) => (
                <Typography
                  key={d}
                  sx={{
                    textAlign: "center",
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    color: theme.customColors?.text?.secondary,
                    py: 0.5,
                    textTransform: "none",
                  }}
                >
                  {d}
                </Typography>
              ))}
            </Box>

            {/* day cells */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: "2px",
              }}
            >
              {calendarDays.map((cell, idx) => {
                const selected = selectedObj && isSameDay(cell.date, selectedObj);
                const isToday = isSameDay(cell.date, today) && !selected;
                const isDisabledDate = isDisabled(cell.date);
                const muted = !cell.current;

                return (
                  <Box
                    key={idx}
                    onClick={() => !isDisabledDate && !muted && handleSelect(cell)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 36,
                      height: 36,
                      mx: "auto",
                      borderRadius: "10px",
                      fontSize: "0.8125rem",
                      fontWeight: selected ? 700 : 500,
                      cursor: isDisabledDate ? "not-allowed" : "pointer",
                      userSelect: "none",
                      position: "relative",
                      transition: "all 0.15s ease",

                      // Selected date
                      ...(selected && {
                        bgcolor: theme.palette._blue?.main || theme.palette.primary.main,
                        color: "#fff",
                      }),

                      // Disabled dates (outside date range)
                      ...(isDisabledDate && !selected && {
                        bgcolor: "#fafafa",
                        color: "#d0d0d0",
                        textDecoration: "line-through",
                        opacity: 0.6,
                      }),

                      // Enabled dates from other months
                      ...(!isDisabledDate && !selected && muted && {
                        color: theme.customColors?.text?.muted,
                      }),

                      // Enabled dates from current month
                      ...(!isDisabledDate && !selected && !muted && {
                        color: theme.customColors?.text?.primary,
                      }),

                      // Today indicator (only for enabled dates)
                      ...(isToday && !isDisabledDate && !selected && {
                        border: `1.5px solid ${theme.customColors?.border?.strong}`,
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          top: 4,
                          right: 4,
                          width: 4,
                          height: 4,
                          borderRadius: "50%",
                          bgcolor: theme.palette._blue?.main || theme.palette.primary.main,
                        },
                      }),

                      // Hover effect (only for enabled dates)
                      ...(!isDisabledDate && !selected && {
                        "&:hover": {
                          bgcolor: theme.customColors?.status?.info?.bg,
                          color: theme.palette._blue?.main || theme.palette.primary.main,
                        },
                      }),
                    }}
                  >
                    {cell.day}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      {/* Helper text */}
      {helperText && (
        <FormHelperText
          error={error}
          sx={{
            mt: 0.5,
            mx: 1.75,
            fontSize: "0.75rem",
          }}
        >
          {helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default DatePicker;
